import React, { useMemo } from 'react';
import { LayoutDashboard, Building2, Users, ClipboardList, FileText, Boxes, Compass, CloudSun, GraduationCap, BrainCircuit, ShieldCheck, Layers, Briefcase, ListChecks, Network, Gavel, BadgeCheck, Sparkles, Heart, Wrench, Languages, Inbox, UserCog, TrendingUp, Library, FlaskConical, Trash2, RotateCcw, SlidersHorizontal, Scale, FolderCheck, DoorOpen, Gauge, UsersRound, Atom } from 'lucide-react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { useApp } from '@/context/AppContext';
import AdminDashboard from '@/pages/admin/AdminDashboard';
import AdminClients from '@/pages/admin/AdminClients';
import AdminClientProfile from '@/pages/admin/AdminClientProfile';
import AdminOrgBoard from '@/pages/admin/AdminOrgBoard';
import AdminUsers from '@/pages/admin/AdminUsers';
import AdminEvaluations from '@/pages/admin/AdminEvaluations';
import AdminEvaluationDetail from '@/pages/admin/AdminEvaluationDetail';
import AdminReports from '@/pages/admin/AdminReports';
import AdminReportReview from '@/pages/admin/AdminReportReview';
import AdminCompetencies from '@/pages/admin/AdminCompetencies';
import AdminResponseScales from '@/pages/admin/AdminResponseScales';
import AdminRoleProfiles from '@/pages/admin/AdminRoleProfiles';
import AdminQuestionnaires from '@/pages/admin/AdminQuestionnaires';
import AdminQuestionnaireDetail from '@/pages/admin/AdminQuestionnaireDetail';
import AdminAssessmentProfile from '@/pages/admin/AdminAssessmentProfile';
import AdminQuestionnaireImport from '@/pages/admin/AdminQuestionnaireImport';
import AdminResourceCenter from '@/pages/admin/AdminResourceCenter';
import AdminStudio from '@/pages/admin/AdminStudio';
import AdminVectorV8 from '@/pages/admin/AdminVectorV8';
import AdminTalentPool from '@/pages/admin/AdminTalentPool';
import AdminStudioEditor from '@/pages/admin/AdminStudioEditor';
import AdminSuites from '@/pages/admin/AdminSuites';
import AdminSuitesAdvisor from '@/pages/admin/AdminSuitesAdvisor';
import AdminTalentMap from '@/pages/admin/AdminTalentMap';
import AdminTalentHeart from '@/pages/admin/AdminTalentHeart';
import AdminTalentFix from '@/pages/admin/AdminTalentFix';
import AdminTalentFixDiagnosticPlan from '@/pages/admin/AdminTalentFixDiagnosticPlan';
import AdminTalentFixInvestigation from '@/pages/admin/AdminTalentFixInvestigation';
import AdminTalentFixSufficiencyRules from '@/pages/admin/AdminTalentFixSufficiencyRules';
import AdminTalentFixOversight from '@/pages/admin/AdminTalentFixOversight';
import AdminTalentStartWorkspace from '@/pages/admin/AdminTalentStartWorkspace';
import AdminTalentStart from '@/pages/admin/AdminTalentStart';
import AdminClimara from '@/pages/admin/AdminClimara';
import AdminTalentTrain from '@/pages/admin/AdminTalentTrain';
import AdminTalentExit from '@/pages/admin/AdminTalentExit';
import AdminTalentPerformance from '@/pages/admin/AdminTalentPerformance';
import AdminSuccession from '@/pages/admin/AdminSuccession';
import AdminRoleBenchmark from '@/pages/admin/AdminRoleBenchmark';
import AdminDemoMigration from '@/pages/admin/AdminDemoMigration';
import AdminDemoHealth from '@/pages/admin/AdminDemoHealth';
import AdminSupportRequests from '@/pages/admin/AdminSupportRequests';
import AdminTalentCompetency from '@/pages/admin/AdminTalentCompetency';
import AdminCompetencyRequests from '@/pages/admin/AdminCompetencyRequests';
import CompetencyProfileEditor from '@/pages/admin/CompetencyProfileEditor';
import AdminIntelligence from '@/pages/admin/AdminIntelligence';
import AdminIntelligenceDepartment from '@/pages/admin/AdminIntelligenceDepartment';
import AdminDepartments from '@/pages/admin/AdminDepartments';
import AdminDecisions from '@/pages/admin/AdminDecisions';
import AdminAudit from '@/pages/admin/AdminAudit';
import AdminTrash from '@/pages/admin/AdminTrash';
import AdminI18nCoverage from '@/pages/admin/AdminI18nCoverage';
import AdminLeads from '@/pages/admin/AdminLeads';
import AdminRecruitment from '@/pages/admin/AdminRecruitment';
import AdminRecruitmentDetail from '@/pages/admin/AdminRecruitmentDetail';
import AdminAdvisors from '@/pages/admin/AdminAdvisors';
import AdminAdvisorDetail from '@/pages/admin/AdminAdvisorDetail';
import AdminCommercialCRM from '@/pages/admin/AdminCommercialCRM';
import AdminCommercialLeadDetail from '@/pages/admin/AdminCommercialLeadDetail';
import AdminCommercialDashboard from '@/pages/admin/AdminCommercialDashboard';
import SuiteConsole from '@/pages/admin/SuiteConsole';
import AdminDemoReset from '@/pages/admin/AdminDemoReset';
import AdminGlobalEthics from '@/pages/admin/AdminGlobalEthics';
import AdminWorkforceRecords from '@/pages/admin/AdminWorkforceRecords';

// Demo Reset is a founder prerogative (backend enforces by user id; this
// mirrors the binding for UI visibility only).
const FOUNDER_EMAIL = 'admin@hmadvisory.com';

const TalentCheckPage = () => (
  <SuiteConsole
    suiteCode="talentcheck" suiteName="TalentCheck"
    suiteTagline="i18n:suite.taglines.talentcheck" accent="info" testid="suite-talentcheck"
    withOps
  />
);
const TalentStartPage = AdminTalentStart;
const TalentHeartPage = AdminTalentHeart;
const TalentFixPage = AdminTalentFix;
const TalentIntelligencePage = () => (
  <SuiteConsole
    suiteCode="intelligence" suiteName="TalentIntelligence"
    suiteTagline="i18n:suite.taglines.talentintelligence" accent="accent"
    testid="suite-talentintelligence" testidPrefix="talentintelligence"
  />
);

const ADMIN_NAV = [
  { label: 'i18n:sidebar.overview', advisorLabel: 'i18n:sidebar.operationalCenter', items: [
    { to: '/admin', label: 'i18n:sidebar.dashboard', icon: LayoutDashboard, end: true },
    { to: '/admin/support-requests', label: 'i18n:sidebar.hmSupport', icon: LayoutDashboard },
    { to: '/admin/intelligence', label: 'i18n:sidebar.talentIntelligence', icon: BrainCircuit, badge: 'AI', advisorFeatured: true, advisorBadge: 'i18n:sidebar.executiveBadge' },
    // UAT 07/18 — 'Registro de Decisiones' nav hidden while Talent Intelligence is FROZEN.
    // Route /admin/intelligence/decisions, data and page remain intact; restore this entry on TI release:
    // { to: '/admin/intelligence/decisions', label: 'i18n:sidebar.decisions', icon: Gavel },
    // BUG 04 — `/admin/departments` removed from top-level; now lives under each client profile
  ]},
  { label: 'i18n:sidebar.catalog', items: [
    { to: '/admin/competencies', label: 'i18n:sidebar.competencies', icon: Layers },
    { to: '/admin/response-scales', label: 'i18n:sidebar.responseScales', icon: SlidersHorizontal },
    { to: '/admin/role-profiles', label: 'i18n:sidebar.roleProfiles', icon: Briefcase },
    { to: '/admin/questionnaires', label: 'i18n:sidebar.questionnaires', icon: ListChecks },
    { to: '/admin/resource-center', label: 'i18n:sidebar.resourceCenter', icon: Library },
  ]},
  { label: 'i18n:sidebar.hmAdministration', items: [
    { to: '/admin/studio', label: 'i18n:sidebar.assessmentStudio', icon: FlaskConical, badge: 'NEW', adminOnly: false },
    { to: '/admin/vector-v8', label: 'Vector V8', icon: Atom, adminOnly: false },
  ]},
  { label: 'i18n:sidebar.management', items: [
    { to: '/admin/clients', label: 'i18n:sidebar.clients', icon: Building2 },
    { to: '/admin/users', label: 'i18n:sidebar.users', icon: Users },
    { to: '/admin/advisors', label: 'i18n:sidebar.advisors', icon: UserCog, adminOnly: true },
    { to: '/admin/crm', label: 'i18n:sidebar.commercialCrm', icon: TrendingUp },
    { to: '/admin/commercial-dashboard', label: 'i18n:sidebar.commercialDashboard', icon: TrendingUp, adminOnly: true },
    { to: '/admin/evaluations', label: 'i18n:sidebar.assignments', icon: ClipboardList },
    { to: '/admin/recruitment', label: 'i18n:sidebar.recruitment', icon: BadgeCheck },
    { to: '/admin/talent-pool', label: 'Talent Pool', icon: Inbox, adminOnly: true },
    { to: '/admin/reports', label: 'i18n:sidebar.reports', icon: FileText },
    { to: '/admin/leads', label: 'i18n:sidebar.leads', icon: Inbox },
    // Global Ethics — standalone module, NOT part of the suite registry.
    { to: '/admin/global-ethics', label: 'Global Ethics', icon: Scale },
    { to: '/admin/workforce-records', label: 'Workforce Records', icon: FolderCheck },
    { to: '/admin/suites', label: 'i18n:sidebar.suites', icon: Boxes },
  ]},
  { label: 'i18n:sidebar.suiteConsoles', advisorLabel: 'i18n:sidebar.operationalSuites', items: [
    { to: '/admin/talentcheck', label: 'TalentCheck', icon: BadgeCheck },
    { to: '/admin/climara', label: 'CLIMARA', icon: CloudSun },
    { to: '/admin/talentmap', label: 'TalentMap', icon: Compass },
    // Functional sequence: TalentCompetency → Competency Gaps → TalentTrain.
    { to: '/admin/talentcompetency', label: 'TalentCompetency', icon: Layers },
    // Phase 3 — RB V2 is PREVIEW-gated until Founder UAT approves it for prod.
    ...(process.env.REACT_APP_ENABLE_RB_V2 === '1'
      ? [{ to: '/admin/role-benchmark', label: 'i18n:sidebar.competencyGaps', icon: Layers }]
      : []),
    { to: '/admin/talenttrain', label: 'TalentTrain', icon: GraduationCap },
    { to: '/admin/talentstart', label: 'TalentStart', icon: Sparkles },
    { to: '/admin/talentheart', label: 'TalentHeart', icon: Heart },
    { to: '/admin/talentfix', label: 'TalentFix', icon: Wrench },
    { to: '/admin/talentexit', label: 'TalentExit', icon: DoorOpen },
    { to: '/admin/talentperformance', label: 'TalentPerformance', icon: Gauge },
    { to: '/admin/succession', label: 'Succession', icon: UsersRound },
    { to: '/admin/talentintelligence', label: 'TalentIntelligence', icon: BrainCircuit },
  ]},
  { label: 'i18n:sidebar.system', items: [
    { to: '/admin/audit', label: 'i18n:sidebar.auditLogs', icon: ShieldCheck },
    { to: '/admin/trash', label: 'i18n:sidebar.trash', icon: Trash2, adminOnly: true },
    { to: '/admin/i18n-coverage', label: 'i18n:sidebar.i18nCoverage', icon: Languages, adminOnly: true },
    { to: '/admin/demo-reset', label: 'Demo Reset', icon: RotateCcw, founderOnly: true },
  ]},
];

// Iter 41.10 — RBAC: hide platform-tooling items from hm_advisor.
// Iter 41.13 (Sesión D) — also swap section labels + activate `featured`
// rendering on items flagged `advisorFeatured`.
function filterNavForRole(nav, role, isFounder) {
  const isAdvisor = role === 'hm_advisor';
  const dropFounderItems = (sections) => sections
    .map((s) => ({ ...s, items: (s.items || []).filter((it) => !it.founderOnly || isFounder) }))
    .filter((s) => (s.items || []).length > 0);
  if (role === 'hm_admin' || role === 'hm_reviewer') return dropFounderItems(nav);
  return dropFounderItems(nav
    .map((section) => ({
      ...section,
      label: isAdvisor && section.advisorLabel ? section.advisorLabel : section.label,
      items: (section.items || [])
        .filter((it) => !it.adminOnly)
        .map((it) => isAdvisor && it.advisorFeatured
          ? { ...it, featured: true, badge: it.advisorBadge ? `i18n:${it.advisorBadge.slice(5)}` : it.badge }
          : it),
    }))
    .filter((section) => (section.items || []).length > 0));
}

export default function AdminRoutes() {
  const { user } = useApp();
  const isFounder = user?.email === FOUNDER_EMAIL;
  const nav = useMemo(() => filterNavForRole(ADMIN_NAV, user?.role, isFounder), [user?.role, isFounder]);
  const isAdvisor = user?.role === 'hm_advisor';
  return (
    <Routes>
      <Route element={<DashboardLayout nav={nav} portalLabel="i18n:layout.portalConsultant" />}>
        <Route index element={<AdminDashboard />} />
        <Route path="clients" element={<AdminClients />} />
        <Route path="clients/:id" element={<AdminClientProfile />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="advisors" element={isAdvisor ? <Navigate to="/admin" replace /> : <AdminAdvisors />} />
        <Route path="advisors/:id" element={isAdvisor ? <Navigate to="/admin" replace /> : <AdminAdvisorDetail />} />
        <Route path="crm" element={<AdminCommercialCRM />} />
        <Route path="crm/:id" element={<AdminCommercialLeadDetail />} />
        <Route path="commercial-dashboard" element={isAdvisor ? <Navigate to="/admin" replace /> : <AdminCommercialDashboard />} />
        <Route path="evaluations" element={<AdminEvaluations />} />
        <Route path="evaluations/:id" element={<AdminEvaluationDetail />} />
        <Route path="reports" element={<AdminReports />} />
        <Route path="reports/:id" element={<AdminReportReview />} />
        <Route path="leads" element={<AdminLeads />} />
        <Route path="global-ethics" element={<AdminGlobalEthics />} />
        <Route path="workforce-records" element={<AdminWorkforceRecords />} />
        <Route path="recruitment" element={<AdminRecruitment />} />
        <Route path="talent-pool" element={<AdminTalentPool />} />
        <Route path="recruitment/:id" element={<AdminRecruitmentDetail />} />
        <Route path="competencies" element={<AdminCompetencies />} />
        <Route path="response-scales" element={<AdminResponseScales />} />
        <Route path="role-profiles" element={<AdminRoleProfiles />} />
        <Route path="questionnaires" element={<AdminQuestionnaires />} />
        <Route path="questionnaires/import" element={<AdminQuestionnaireImport />} />
        <Route path="questionnaires/:id" element={<AdminQuestionnaireDetail />} />
        <Route path="library/:id" element={<AdminAssessmentProfile />} />
        <Route path="resource-center" element={<AdminResourceCenter />} />
        {/* Phase 1 — Assessment Studio shell (single internal CTH workspace). */}
        <Route path="studio" element={<AdminStudio />} />
        <Route path="vector-v8" element={<AdminVectorV8 />} />
        <Route path="studio/edit/:id" element={<AdminStudioEditor />} />
        {/* Legacy entrypoint kept for backwards-compat — points to the Evaluation Library. */}
        <Route path="evaluation-library" element={<AdminQuestionnaires />} />
        <Route path="evaluation-library/:id" element={<AdminQuestionnaireDetail />} />
        <Route path="suites" element={isAdvisor ? <AdminSuitesAdvisor /> : <AdminSuites />} />
        <Route path="talentcheck" element={<TalentCheckPage />} />
        <Route path="talentmap" element={<AdminTalentMap />} />
        <Route path="talentmap/:assignmentId" element={<AdminTalentMap />} />
        <Route path="climara" element={<AdminClimara />} />
        <Route path="talenttrain" element={<AdminTalentTrain />} />
        <Route path="talenttrain/:userId" element={<AdminTalentTrain />} />
        <Route path="talentexit" element={<AdminTalentExit />} />
        <Route path="talentperformance" element={<AdminTalentPerformance />} />
        <Route path="succession" element={<AdminSuccession />} />
        {process.env.REACT_APP_ENABLE_RB_V2 === '1' && (
          <Route path="role-benchmark" element={<AdminRoleBenchmark />} />
        )}
        <Route path="support-requests" element={<AdminSupportRequests />} />
        {/* hm_admin-only governed Novera demo-data migration console (direct URL, no menu entry). */}
        <Route path="demo-migration" element={<AdminDemoMigration />} />
        <Route path="demo-health" element={<AdminDemoHealth />} />
        <Route path="talentcompetency" element={<AdminTalentCompetency />} />
        <Route path="talentcompetency/requests" element={<AdminCompetencyRequests />} />
        <Route path="talentcompetency/profiles/:id" element={<CompetencyProfileEditor />} />
        <Route path="talentstart" element={<TalentStartPage />} />
        <Route path="talentheart" element={<TalentHeartPage />} />
        <Route path="talentfix" element={<TalentFixPage />} />
        <Route path="talentfix/oversight" element={<AdminTalentFixOversight />} />
        <Route path="talentfix/cases/:id/diagnostic-plan" element={<AdminTalentFixDiagnosticPlan />} />
        <Route path="talentfix/cases/:id/investigation" element={<AdminTalentFixInvestigation />} />
        <Route path="talentfix/sufficiency-rules" element={<AdminTalentFixSufficiencyRules />} />
        <Route path="talentintelligence" element={<TalentIntelligencePage />} />
        <Route path="intelligence" element={<AdminIntelligence />} />
        <Route path="intelligence/decisions" element={<AdminDecisions />} />
        <Route path="intelligence/departments/:deptId" element={<AdminIntelligenceDepartment />} />
        <Route path="departments" element={<AdminDepartments />} />
        <Route path="org-board" element={<AdminOrgBoard />} />
        <Route path="audit" element={<AdminAudit />} />
        <Route path="demo-reset" element={isFounder ? <AdminDemoReset /> : <Navigate to="/admin" replace />} />
        <Route path="trash" element={<AdminTrash />} />
        <Route
          path="i18n-coverage"
          element={isAdvisor ? <Navigate to="/admin" replace /> : <AdminI18nCoverage />}
        />
        <Route path="*" element={<Navigate to="/admin" replace /> } />
      </Route>
    </Routes>
  );
}
